
import {
  defineComponent, reactive, computed, watch,
  ref,
} from 'vue';
import { useAppBase } from '@/core/composables/AppBase';
import { ICadastro } from '@/models/Entidades/ICadastro';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import ServicoCadastro from '@/servicos/ServicoCadastro';
import { EStatusResposta, IResposta } from '@/core/models/IResposta';
import Card from '@/core/components/UI/Card.vue';
import Icone from '@/core/components/UI/Icone.vue';
import CampoCpf from '@/core/components/UI/CampoCpf.vue';
import CampoCep from '@/core/components/UI/CampoCep.vue';
import ComboCidade from '@/components/ComboCidade.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import SelecionarSimNao from '@/core/components/UI/SelecionarSimNao.vue';
import storeApp from '@/store/storeApp';
import { ETipoRelacaoCadastro } from '@/models/Enumeradores/ETipoRelacaoCadastro';
import CampoTelefone from '@/core/components/UI/CampoTelefone.vue';
import CampoEmail from '@/core/components/UI/CampoEmail.vue';
import { ETipoDocumento } from '@/models/Enumeradores/ETipoDocumento';
import ComboTipoDocumento from '@/components/ComboTipoDocumento.vue';
import CampoCnpj from '@/core/components/UI/CampoCnpj.vue';
import { IEndereco } from '@/models/Entidades/IEndereco';
import { ETipoVinculo } from '@/models/Enumeradores/ETipoVinculo';
import ServicoUtilitario from '@/servicos/ServicoUtilitario';
import ServicoApp from '@/servicos/ServicoApp';

export default defineComponent({
  name: 'CadastroSeguradoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    codigoCadastro: {
      type: Number,
      default: 0,
    },
  },
  components: {
    ComunicacaoApi,
    Card,
    ComboTipoDocumento,
    CampoCpf,
    CampoCnpj,
    CampoCep,
    CampoTelefone,
    CampoEmail,
    SelecionarSimNao,
    ComboCidade,
    Icone,
  },
  emits: ['operacaoConcluida', 'update:visivel'],
  setup(props, { emit }) {
    const {
      appBase, apresentarComunicacaoApi, ocultarComunicacaoApi, apresentarResposta,
    } = useAppBase();
    const servicoCadastro = new ServicoCadastro();
    const servicoUtilitario = new ServicoUtilitario();
    const consultandoDocumento = ref<boolean>(false);
    const consultandoCep = ref<boolean>(false);
    const state = reactive({
      segurado: {} as ICadastro,
      cpfCnpj: '',
      cep: '',
      apresentarCampos: false,
      numeroDocumentoHistorico: '',
      enviandoImagem: false,
      dragOverImagem: false,
      computedVisivel: computed({
        get: () => props.visivel,
        set: (val: boolean) => {
          emit('update:visivel', val);
        },
      }),
    });

    const possuiImagem = computed(() => UtilitarioGeral.valorValido(state.segurado.imagem));
    function limparDados() {
      consultandoDocumento.value = false;
      consultandoCep.value = false;
      state.segurado = {} as ICadastro;
      state.segurado.codigo = 0;
      state.segurado.tipo = ETipoRelacaoCadastro.Segurado;
      state.segurado.tipoDocumento = ETipoDocumento.CNPJ;
      state.segurado.status = true;
      state.segurado.endereco = {} as IEndereco;
      state.segurado.endereco.codigo = 0;
      state.segurado.endereco.codigoParceiro = 0;
      state.segurado.endereco.codigoVinculo = 0;
      state.segurado.endereco.tipoVinculo = ETipoVinculo.Cadastro;
      state.cpfCnpj = '';
      state.cep = '';
      state.apresentarCampos = true;
    }

    async function consultaCep() {
      if (UtilitarioGeral.valorValido(state.segurado.endereco.cep) && UtilitarioGeral.validaValorComLimiteCaracteres(state.segurado.endereco.cep, 8)) {
        if (state.segurado.endereco.cep !== state.cep) {
          consultandoCep.value = true;
          const retornoConsultaCep = await servicoUtilitario.consultaCep(state.segurado.endereco.cep);
          if (retornoConsultaCep !== undefined) {
            state.segurado.endereco.enderecoCompleto = retornoConsultaCep.logradouro;
            state.segurado.endereco.complemento = retornoConsultaCep.complemento;
            state.segurado.endereco.bairro = retornoConsultaCep.bairro;
            state.segurado.endereco.codigoCidade = retornoConsultaCep.codigoCidade;
            state.cep = state.segurado.endereco.cep;
          }
        }
      }

      consultandoCep.value = false;
    }

    async function consultaDocumento() {
      if (state.segurado.tipoDocumento === ETipoDocumento.CNPJ) {
        if (UtilitarioGeral.valorValido(state.segurado.numeroDocumento) && UtilitarioGeral.validaValorComLimiteCaracteres(state.segurado.numeroDocumento, 14)) {
          if (state.segurado.numeroDocumento !== state.numeroDocumentoHistorico) {
            consultandoDocumento.value = true;
            const retornoConsultaCnpj = await servicoUtilitario.consultaCnpj(state.segurado.numeroDocumento);
            consultandoDocumento.value = false;
            if (retornoConsultaCnpj !== undefined) {
              if (UtilitarioGeral.valorValido(retornoConsultaCnpj.razaoSocial)) {
                state.segurado.nomeRazaoSocial = retornoConsultaCnpj.razaoSocial;
                state.segurado.nomeFantasia = retornoConsultaCnpj.nomeFantasia;
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.contato.telefonePrincipal)) {
                  state.segurado.telefone = retornoConsultaCnpj.contato.telefonePrincipal;
                }
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.contato.email)) {
                  state.segurado.email = retornoConsultaCnpj.contato.email;
                }
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.endereco.cep)) {
                  state.segurado.endereco.cep = retornoConsultaCnpj.endereco.cep;
                }
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.endereco.logadouro)) {
                  state.segurado.endereco.enderecoCompleto = retornoConsultaCnpj.endereco.logadouro;
                }
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.endereco.numero)) {
                  if (retornoConsultaCnpj.endereco.numero !== 'S/N' && retornoConsultaCnpj.endereco.numero !== 'SN') {
                    state.segurado.endereco.numero = retornoConsultaCnpj.endereco.numero;
                  }
                }
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.endereco.bairro)) {
                  state.segurado.endereco.bairro = retornoConsultaCnpj.endereco.bairro;
                }
                if (UtilitarioGeral.valorValido(retornoConsultaCnpj.endereco.complemento)) {
                  state.segurado.endereco.complemento = retornoConsultaCnpj.endereco.complemento;
                }

                if (UtilitarioGeral.validaCodigo(retornoConsultaCnpj.endereco.codigoCidade)) {
                  state.segurado.endereco.codigoCidade = retornoConsultaCnpj.endereco.codigoCidade;
                }
                state.numeroDocumentoHistorico = state.segurado.numeroDocumento;
              }
            }
          }
        }
      }
    }

    async function obterCadastro(codigo:number) {
      appBase.carregando = true;
      state.segurado = await servicoCadastro.obter(codigo);
      state.cpfCnpj = state.segurado.numeroDocumento;

      appBase.carregando = false;
    }

    watch(async () => state.computedVisivel, async () => {
      appBase.carregando = true;
      console.log('chegou para abrir', props.codigoCadastro);
      limparDados();
      if (state.computedVisivel) {
        if (props.codigoCadastro > 0) {
          await obterCadastro(props.codigoCadastro);
        }
      }
      appBase.carregando = false;
    });

    async function salvar() {
      appBase.resposta = {} as IResposta;

      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações da segurado.');
      if (state.segurado.codigo === 0) {
        appBase.resposta = await servicoCadastro.incluir(state.segurado);
      } else {
        appBase.resposta = await servicoCadastro.atualizar(state.segurado);
      }
      if (appBase.resposta.status === EStatusResposta.Sucesso) {
        emit('operacaoConcluida', appBase.resposta.codigo);
        ocultarComunicacaoApi();
        state.computedVisivel = false;
      } else {
        apresentarResposta();
      }
    }

    function removerImagem() {
      state.segurado.imagem = '';
      const elemento = document.getElementById('selecionarArquivo') as HTMLInputElement | null;
      if (elemento !== null) {
        elemento.value = '';
      }
    }

    function validarArquivoImagem(arquivo: File): boolean {
      const tiposPermitidos = ['image/png', 'image/jpeg', 'image/webp'];

      if (!tiposPermitidos.includes(arquivo.type)) {
        apresentarComunicacaoApi(appBase.resposta.mensagens[0].subMensagens[0]);
        return false;
      }

      return true;
    }

    async function processarUpload(arquivos: FileList | File[]) {
      if (!UtilitarioGeral.validaLista(arquivos)) {
        return;
      }

      const arquivo = arquivos[0];
      if (!arquivo || !validarArquivoImagem(arquivo)) {
        return;
      }

      state.enviandoImagem = true;

      try {
        const listaArquivos = await new ServicoApp().enviarVarios([arquivo]);

        listaArquivos.forEach((item) => {
          if (UtilitarioGeral.valorValido(item.linkArquivo)) {
            state.segurado.imagem = item.linkArquivo;
          }
        });
      } finally {
        state.enviandoImagem = false;
        state.dragOverImagem = false;
      }
    }

    async function enviarArquivos(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files) {
        await processarUpload(input.files);
      }
    }

    async function dropArquivo(event: DragEvent) {
      state.dragOverImagem = false;

      if (event.dataTransfer?.files) {
        await processarUpload(event.dataTransfer.files);
      }
    }

    function selecionarArquivoUpload() {
      const elemento = document.getElementById('selecionarArquivo');
      if (elemento !== null) {
        elemento.click();
      }
    }

    return {
      appBase,
      storeApp,
      state,
      salvar,
      UtilitarioGeral,
      ETipoDocumento,
      consultaCep,
      consultaDocumento,
      consultandoCep,
      consultandoDocumento,
      dropArquivo,
      enviarArquivos,
      possuiImagem,
      removerImagem,
      selecionarArquivoUpload,
    };
  },
});
